import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { Card, Modal, Button, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAlert } from 'react-alert';
import { FaPlus, FaTrash, FaRegCopy, FaChevronUp, FaChevronDown, FaListOl } from 'react-icons/fa';

import MenuFuncionario from '../../../componentes/MenuFuncinario';
import Footer from '../../../componentes/Footer/index';
import api from '../../../services/api';
import { Container, TabelaItens, Botoes } from './styles';

// ── Tipos de item aceitos pela agenda ──────────────────────────────────────
// Os valores gravados em agenda_modelo_detalhe.tipo sao lidos literalmente
// (com acento) pelo app-capacitor em AgendaConteudoCadastro/AgendaGrupoCadastro
// e pela consulta do responsavel. Nao alterar as strings.
const TIPOS_ITEM = [
  { value: 'CABEÇALHO', label: 'CABEÇALHO', ajuda: 'Apenas um titulo de secao, sem resposta.' },
  { value: 'TEXTO', label: 'TEXTO', ajuda: 'Campo livre para o professor escrever.' },
  { value: 'PRÉ-TEXTO', label: 'PRÉ-TEXTO', ajuda: 'Campo livre que ja vem preenchido com um texto padrao.' },
  { value: 'MARCAÇÃO', label: 'MARCAÇÃO', ajuda: 'Chave liga/desliga (SIM ou NÃO).' },
  { value: 'OPÇÃO', label: 'OPÇÃO', ajuda: 'SIM / NÃO / AS VEZES.' },
  { value: 'NOTA', label: 'NOTA (1 a 5)', ajuda: 'Escala de 1 a 5 com os textos da aba "Escala de Nota".' },
  { value: 'QUANTIDADE', label: 'QUANTIDADE', ajuda: 'Contador numerico com + e -.' },
  { value: 'REFEIÇÃO', label: 'REFEIÇÃO', ajuda: 'RECUSOU ate REPETIU.' },
  { value: 'FOTOS', label: 'FOTOS', ajuda: 'Envio de uma ou mais fotos.' },
];

const CODS_NOTA = ['1', '2', '3', '4', '5'];

// Textos que hoje estao fixos no app; servem de sugestao ao montar a escala.
const NOTA_PADRAO = {
  '1': 'NÃO ATENDE',
  '2': 'INSUFICIENTE',
  '3': 'REGULAR',
  '4': 'BOM',
  '5': 'EXCELENTE',
};

const ESCALA_VAZIA = { '1': '', '2': '', '3': '', '4': '', '5': '' };

// Opcao sem texto nao vira linha em agenda_convencoes, e o app so lista as que
// existem -- entao preencher 3 de 5 e um caso valido, nao um erro.
function opcoesPreenchidas(escala) {
  return CODS_NOTA.filter(cod => (escala[cod] || '').trim() !== '').length;
}

const MODELO_VAZIO = { id: null, codmod: '', desmod: '' };

function AgendaModelos({ history, escola, usuario, periodo }) {
  const alert = useAlert();
  const autorizado = !!usuario && usuario.admapp === 'S';

  const chaveKey = useRef(0);
  const proximaKey = () => { chaveKey.current += 1; return `k${chaveKey.current}`; };

  const [aba, setAba] = useState('dados');
  const [modelos, setModelos] = useState([]);
  const [selecionado, setSelecionado] = useState(null); // codmod do modelo aberto
  const [dados, setDados] = useState(MODELO_VAZIO);

  const [itens, setItens] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [turmasSel, setTurmasSel] = useState([]);

  // Escala aberta no modal: { key, escala } — a edicao acontece numa copia e so
  // volta para o item ao confirmar, para o Cancelar realmente desfazer.
  const [escalaAberta, setEscalaAberta] = useState(null);

  const [showNovo, setShowNovo] = useState(false);
  const [novo, setNovo] = useState({ codmod: '', desmod: '' });
  const [showDuplicar, setShowDuplicar] = useState(false);
  const [duplicar, setDuplicar] = useState({ novoCodmod: '', novoDesmod: '' });
  const [showExcluir, setShowExcluir] = useState(false);

  document.title = 'Modelos de Agenda';

  const chave = useCallback(() => ({
    codigo: escola.codigo,
    escola: periodo.escola,
    ano: periodo.ano,
    seqano: periodo.seqano,
  }), [escola, periodo]);

  // Os endpoints devolvem { erro: '...' } com status 200; centraliza a leitura.
  function erroDe(res) {
    return res && res.data && res.data.erro ? res.data.erro : null;
  }

  // ── Carga ────────────────────────────────────────────────────────────────
  const carregarModelos = useCallback(async () => {
    try {
      const res = await api.get('/agenda/modelos', { params: chave() });
      const erro = erroDe(res);
      if (erro) { alert.error(erro); return []; }
      const lista = Array.isArray(res.data) ? res.data : [];
      setModelos(lista);
      return lista;
    } catch (err) {
      alert.error('Erro ao carregar os modelos de agenda.');
      return [];
    }
  }, [chave, alert]);

  useEffect(() => {
    if (!autorizado) return;
    carregarModelos();
    // eslint-disable-next-line
  }, [autorizado]);

  // As convencoes vem numa lista plana (numite, cod, des); aqui viram um mapa
  // por numite para casar com cada item.
  function agruparEscalas(linhas) {
    const porNumite = {};
    (Array.isArray(linhas) ? linhas : []).forEach(c => {
      const n = String(c.numite);
      if (!porNumite[n]) porNumite[n] = { ...ESCALA_VAZIA };
      porNumite[n][String(c.cod)] = c.des || '';
    });
    return porNumite;
  }

  function montarItens(listaItens, escalas) {
    return (Array.isArray(listaItens) ? listaItens : []).map(i => ({
      key: proximaKey(),
      numite: i.numite,
      item: i.item || '',
      tipo: i.tipo || 'TEXTO',
      pretexto: i.pretexto || '',
      escala: escalas[String(i.numite)] || { ...ESCALA_VAZIA },
    }));
  }

  async function abrirModelo(modelo) {
    setSelecionado(modelo.codmod);
    setDados({
      id: modelo.id,
      codmod: modelo.codmod,
      desmod: modelo.desmod || '',
    });
    setAba('dados');

    const params = { ...chave(), codmod: modelo.codmod };

    try {
      const [resItens, resConv, resTurmas] = await Promise.all([
        api.get('/agenda/modelo/itens', { params }),
        api.get('/agenda/modelo/convencoes', { params: { ...params, tipo: 'NOTA' } }),
        api.get('/agenda/modelo/turmas', { params: chave() }),
      ]);

      setItens(montarItens(resItens.data, agruparEscalas(resConv.data)));

      const listaTurmas = Array.isArray(resTurmas.data) ? resTurmas.data : [];
      setTurmas(listaTurmas);
      setTurmasSel(listaTurmas.filter(t => t.codmod === modelo.codmod).map(t => t.turma));
    } catch (err) {
      alert.error('Erro ao carregar o modelo selecionado.');
    }
  }

  function fecharModelo() {
    setSelecionado(null);
    setDados(MODELO_VAZIO);
    setItens([]);
    setEscalaAberta(null);
    setTurmas([]);
    setTurmasSel([]);
  }

  // ── Aba Dados ────────────────────────────────────────────────────────────
  async function salvarNovo() {
    if (!novo.codmod.trim()) { alert.error('Informe o código do modelo.'); return; }
    if (!novo.desmod.trim()) { alert.error('Informe a descrição do modelo.'); return; }

    try {
      const res = await api.post('/agenda/modelo/save', { ...chave(), ...novo });
      const erro = erroDe(res);
      if (erro) { alert.error(erro); return; }

      setShowNovo(false);
      setNovo({ codmod: '', desmod: '' });
      alert.success('Modelo criado com sucesso!');

      const lista = await carregarModelos();
      const criado = lista.find(m => m.codmod === res.data.codmod);
      if (criado) abrirModelo(criado);
    } catch (err) {
      alert.error('Erro ao criar o modelo.');
    }
  }

  async function salvarDados() {
    if (!dados.desmod.trim()) { alert.error('Informe a descrição do modelo.'); return; }

    try {
      const res = await api.post('/agenda/modelo/save', { ...chave(), ...dados });
      const erro = erroDe(res);
      if (erro) { alert.error(erro); return; }
      alert.success('Dados do modelo salvos!');
      carregarModelos();
    } catch (err) {
      alert.error('Erro ao salvar o modelo.');
    }
  }

  async function confirmarExcluir() {
    try {
      const res = await api.post('/agenda/modelo/delete', { ...chave(), codmod: dados.codmod });
      const erro = erroDe(res);
      if (erro) { alert.error(erro); return; }
      setShowExcluir(false);
      alert.success('Modelo excluído.');
      fecharModelo();
      carregarModelos();
    } catch (err) {
      alert.error('Erro ao excluir o modelo.');
    }
  }

  async function confirmarDuplicar() {
    if (!duplicar.novoCodmod.trim()) { alert.error('Informe o código do novo modelo.'); return; }
    if (!duplicar.novoDesmod.trim()) { alert.error('Informe a descrição do novo modelo.'); return; }

    try {
      const res = await api.post('/agenda/modelo/duplicar', {
        ...chave(), codmod: dados.codmod, ...duplicar,
      });
      const erro = erroDe(res);
      if (erro) { alert.error(erro); return; }

      setShowDuplicar(false);
      setDuplicar({ novoCodmod: '', novoDesmod: '' });
      alert.success('Modelo duplicado! As turmas não são copiadas — vincule na aba Turmas.');

      const lista = await carregarModelos();
      const criado = lista.find(m => m.codmod === res.data.codmod);
      if (criado) abrirModelo(criado);
    } catch (err) {
      alert.error('Erro ao duplicar o modelo.');
    }
  }

  // ── Aba Itens ────────────────────────────────────────────────────────────
  function adicionarItem() {
    setItens(prev => [...prev, {
      key: proximaKey(), numite: null, item: '', tipo: 'TEXTO',
      pretexto: '', escala: { ...ESCALA_VAZIA },
    }]);
  }

  function alterarItem(key, campo, valor) {
    setItens(prev => prev.map(i => (i.key === key ? { ...i, [campo]: valor } : i)));
  }

  function removerItem(key) {
    setItens(prev => prev.filter(i => i.key !== key));
  }

  function abrirEscala(item) {
    setEscalaAberta({ key: item.key, escala: { ...ESCALA_VAZIA, ...(item.escala || {}) } });
  }

  function alterarEscala(cod, valor) {
    setEscalaAberta(prev => ({ ...prev, escala: { ...prev.escala, [cod]: valor } }));
  }

  function usarTextosPadrao() {
    setEscalaAberta(prev => ({ ...prev, escala: { ...NOTA_PADRAO } }));
  }

  function limparEscala() {
    setEscalaAberta(prev => ({ ...prev, escala: { ...ESCALA_VAZIA } }));
  }

  // So aplica no item; a gravacao acontece junto com "Salvar itens".
  function confirmarEscala() {
    const { key, escala } = escalaAberta;
    setItens(prev => prev.map(i => (i.key === key ? { ...i, escala } : i)));
    setEscalaAberta(null);
  }

  function moverItem(index, direcao) {
    const destino = index + direcao;
    if (destino < 0 || destino >= itens.length) return;
    const lista = [...itens];
    const [movido] = lista.splice(index, 1);
    lista.splice(destino, 0, movido);
    setItens(lista);
  }

  async function salvarItens() {
    const semTexto = itens.some(i => !i.item.trim());
    if (semTexto) { alert.error('Existe item sem descrição. Preencha ou remova.'); return; }

    const notaSemEscala = itens.find(i => i.tipo === 'NOTA' && opcoesPreenchidas(i.escala || {}) === 0);
    if (notaSemEscala) {
      alert.error(`O item "${notaSemEscala.item.trim()}" é do tipo NOTA e está sem nenhuma resposta na escala.`);
      return;
    }

    try {
      const res = await api.post('/agenda/modelo/itens/save', {
        ...chave(),
        codmod: dados.codmod,
        itens: itens.map(i => ({
          numite: i.numite,
          item: i.item.trim(),
          tipo: i.tipo,
          pretexto: i.tipo === 'PRÉ-TEXTO' ? i.pretexto : '',
          // Opcao em branco nao e enviada: e assim que o item fica com menos
          // de 5 respostas, e o app lista so as que existem.
          escala: i.tipo === 'NOTA'
            ? CODS_NOTA
              .map(cod => ({ cod, des: ((i.escala || {})[cod] || '').trim() }))
              .filter(o => o.des !== '')
            : [],
        })),
      });
      const erro = erroDe(res);
      if (erro) { alert.error(erro); return; }

      alert.success('Itens salvos com sucesso!');
      carregarModelos();

      // Recarrega para trazer o numite dos itens novos e a escala ja gravada.
      const params = { ...chave(), codmod: dados.codmod };
      const [recargaItens, recargaConv] = await Promise.all([
        api.get('/agenda/modelo/itens', { params }),
        api.get('/agenda/modelo/convencoes', { params: { ...params, tipo: 'NOTA' } }),
      ]);
      setItens(montarItens(recargaItens.data, agruparEscalas(recargaConv.data)));
    } catch (err) {
      alert.error('Erro ao salvar os itens.');
    }
  }

  // ── Aba Turmas ───────────────────────────────────────────────────────────
  function alternarTurma(turma) {
    setTurmasSel(prev => (
      prev.indexOf(turma) >= 0 ? prev.filter(t => t !== turma) : [...prev, turma]
    ));
  }

  async function salvarTurmas() {
    try {
      const res = await api.post('/agenda/modelo/turmas/vincular', {
        ...chave(),
        codmod: dados.codmod,
        turmas: turmasSel,
        guerra: usuario.guerra,
      });
      const erro = erroDe(res);
      if (erro) { alert.error(erro); return; }

      alert.success('Turmas vinculadas!');
      carregarModelos();

      const recarga = await api.get('/agenda/modelo/turmas', { params: chave() });
      setTurmas(Array.isArray(recarga.data) ? recarga.data : []);
    } catch (err) {
      alert.error('Erro ao vincular as turmas.');
    }
  }

  // ── Sem permissão ────────────────────────────────────────────────────────
  if (!autorizado) {
    return (
      <Container>
        <MenuFuncionario history={history} />
        <Card className="card-main">
          <Card.Body>
            <div className="vazio">
              <h5>Acesso restrito</h5>
              <p className="mb-3">
                Esta tela é liberada apenas para usuários administradores do aplicativo.
              </p>
              <Link to="/homefuncionario" className="btn btn-sm btn-primary">Voltar para o início</Link>
            </div>
          </Card.Body>
        </Card>
        <Footer />
      </Container>
    );
  }

  return (
    <Container>
      <MenuFuncionario history={history} />
      <Card className="card-main">
        <Card.Body>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><Link to="/agenda">Agenda</Link></li>
              <li className="breadcrumb-item active" aria-current="page">Modelos de Agenda</li>
            </ol>
          </nav>

          <div className="row">
            {/* ── Lista de modelos ───────────────────────────────────────── */}
            <div className="col-md-4">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <strong>Modelos ({modelos.length})</strong>
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  onClick={() => { setNovo({ codmod: '', desmod: '' }); setShowNovo(true); }}
                >
                  <FaPlus /> Novo
                </button>
              </div>

              <div className="lista-modelos">
                {modelos.length === 0 && (
                  <div className="vazio">
                    <p className="mb-0">Nenhum modelo cadastrado neste período.</p>
                  </div>
                )}

                {modelos.map(m => (
                  <div
                    key={m.id}
                    className={`modelo-item ${selecionado === m.codmod ? 'ativo' : ''}`}
                    onClick={() => abrirModelo(m)}
                  >
                    <div>
                      <span className="codmod">{m.codmod}</span>
                      <span className="desmod">{m.desmod}</span>
                    </div>
                    <div className="meta">
                      {m.qtd_itens} {Number(m.qtd_itens) === 1 ? 'item' : 'itens'}
                      {' · '}{m.qtd_turmas} {Number(m.qtd_turmas) === 1 ? 'turma' : 'turmas'}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Editor ─────────────────────────────────────────────────── */}
            <div className="col-md-8">
              {!selecionado ? (
                <div className="vazio">
                  <h5>Nenhum modelo aberto</h5>
                  <p className="mb-0">
                    Selecione um modelo na lista ao lado ou clique em <strong>Novo</strong> para criar.
                  </p>
                </div>
              ) : (
                <>
                  <ul className="nav nav-tabs">
                    <li className="nav-item">
                      <span className={`nav-link ${aba === 'dados' ? 'active' : ''}`} onClick={() => setAba('dados')}>Dados</span>
                    </li>
                    <li className="nav-item">
                      <span className={`nav-link ${aba === 'itens' ? 'active' : ''}`} onClick={() => setAba('itens')}>
                        Itens ({itens.length})
                      </span>
                    </li>
                    <li className="nav-item">
                      <span className={`nav-link ${aba === 'turmas' ? 'active' : ''}`} onClick={() => setAba('turmas')}>
                        Turmas ({turmasSel.length})
                      </span>
                    </li>
                  </ul>

                  <div className="aba-conteudo">
                    {/* ── Dados ─────────────────────────────────────────── */}
                    {aba === 'dados' && (
                      <>
                        <div className="form-row">
                          <div className="form-group col-md-3">
                            <label>Código</label>
                            <input type="text" className="form-control" value={dados.codmod} disabled readOnly />
                            <small className="form-text text-muted">
                              Não pode ser alterado: liga itens, turmas e lançamentos já gravados.
                            </small>
                          </div>
                          <div className="form-group col-md-9">
                            <label>Descrição</label>
                            <input
                              type="text"
                              className="form-control"
                              maxLength={120}
                              value={dados.desmod}
                              onChange={e => setDados({ ...dados, desmod: e.target.value })}
                            />
                          </div>
                        </div>

                        <Botoes>
                          <button type="button" className="btn btn-sm btn-outline-danger mr-auto ml-0" onClick={() => setShowExcluir(true)}>
                            <FaTrash /> Excluir modelo
                          </button>
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setDuplicar({ novoCodmod: '', novoDesmod: `${dados.desmod} (cópia)` }); setShowDuplicar(true); }}>
                            <FaRegCopy /> Duplicar
                          </button>
                          <button type="button" className="btn btn-sm btn-primary" onClick={salvarDados}>Salvar</button>
                        </Botoes>
                      </>
                    )}

                    {/* ── Itens ─────────────────────────────────────────── */}
                    {aba === 'itens' && (
                      <>
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <small className="text-muted">
                            A ordem abaixo é a ordem que o professor vê no aplicativo.
                          </small>
                          <button type="button" className="btn btn-sm btn-success" onClick={adicionarItem}>
                            <FaPlus /> Adicionar item
                          </button>
                        </div>

                        {itens.length === 0 ? (
                          <div className="vazio">
                            <p className="mb-0">Nenhum item neste modelo. Clique em <strong>Adicionar item</strong>.</p>
                          </div>
                        ) : (
                          <TabelaItens className="table table-sm table-bordered">
                            <thead className="thead-light">
                              <tr>
                                <th className="col-ordem">Ordem</th>
                                <th className="col-numite">Nº</th>
                                <th>Item</th>
                                <th className="col-tipo">Tipo</th>
                                <th className="col-acao"> </th>
                              </tr>
                            </thead>
                            <tbody>
                              {itens.map((item, index) => (
                                <tr key={item.key} className={item.tipo === 'CABEÇALHO' ? 'linha-cabecalho' : ''}>
                                  <td className="col-ordem">
                                    <button type="button" className="btn-ordem" disabled={index === 0} onClick={() => moverItem(index, -1)}>
                                      <FaChevronUp />
                                    </button>
                                    <button type="button" className="btn-ordem" disabled={index === itens.length - 1} onClick={() => moverItem(index, 1)}>
                                      <FaChevronDown />
                                    </button>
                                  </td>
                                  <td className="col-numite">{item.numite || 'novo'}</td>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      maxLength={150}
                                      placeholder="Texto da pergunta / do título"
                                      value={item.item}
                                      onChange={e => alterarItem(item.key, 'item', e.target.value)}
                                    />
                                    {item.tipo === 'PRÉ-TEXTO' && (
                                      <textarea
                                        className="form-control form-control-sm mt-1"
                                        rows={2}
                                        placeholder="Texto que já vem preenchido para o professor"
                                        value={item.pretexto}
                                        onChange={e => alterarItem(item.key, 'pretexto', e.target.value)}
                                      />
                                    )}
                                  </td>
                                  <td className="col-tipo">
                                    <select
                                      className="form-control form-control-sm"
                                      value={item.tipo}
                                      onChange={e => alterarItem(item.key, 'tipo', e.target.value)}
                                    >
                                      {TIPOS_ITEM.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                  </td>
                                  <td className="col-acao">
                                    {/* So icone: o texto empurrava a linha para uma segunda
                                        altura. A contagem vai no title e na cor do botao. */}
                                    {item.tipo === 'NOTA' && (() => {
                                      const total = opcoesPreenchidas(item.escala || {});
                                      return (
                                        <button
                                          type="button"
                                          className={`btn btn-sm ${total === 0 ? 'btn-outline-warning' : 'btn-outline-primary'}`}
                                          title={total === 0
                                            ? 'Definir as respostas da escala deste item'
                                            : `Respostas da escala: ${total} de 5 preenchidas`}
                                          onClick={() => abrirEscala(item)}
                                        >
                                          <FaListOl />
                                        </button>
                                      );
                                    })()}
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      title="Remover item"
                                      onClick={() => removerItem(item.key)}
                                    >
                                      <FaTrash />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </TabelaItens>
                        )}

                        <Botoes>
                          <button type="button" className="btn btn-sm btn-primary" onClick={salvarItens}>Salvar itens</button>
                        </Botoes>
                      </>
                    )}

                    {/* ── Turmas ────────────────────────────────────────── */}
                    {aba === 'turmas' && (
                      <>
                        <p className="text-muted mb-2" style={{ fontSize: 13 }}>
                          Marque as turmas que usam este modelo. Cada turma tem no máximo um
                          modelo: marcar uma turma que já está em outro modelo a move para este.
                        </p>

                        {turmas.length === 0 ? (
                          <div className="vazio">
                            <p className="mb-0">Nenhuma turma encontrada no período.</p>
                          </div>
                        ) : (
                          <table className="table table-sm table-bordered mb-0">
                            <thead className="thead-light">
                              <tr>
                                <th style={{ width: 40, textAlign: 'center' }}> </th>
                                <th>Turma</th>
                                <th style={{ width: 70, textAlign: 'center' }}>Alunos</th>
                                <th style={{ width: 220 }}>Modelo atual</th>
                              </tr>
                            </thead>
                            <tbody>
                              {turmas.map(t => {
                                const marcada = turmasSel.indexOf(t.turma) >= 0;
                                const deOutro = t.codmod && t.codmod !== dados.codmod;
                                return (
                                  <tr key={t.turma}>
                                    <td style={{ textAlign: 'center' }}>
                                      <input type="checkbox" checked={marcada} onChange={() => alternarTurma(t.turma)} />
                                    </td>
                                    <td>{t.destur}</td>
                                    <td style={{ textAlign: 'center' }}>{t.qtd}</td>
                                    <td>
                                      {!t.codmod && <span className="text-muted">—</span>}
                                      {deOutro && (
                                        <span className="badge badge-warning">
                                          {t.codmod} - {t.desmod || 'modelo removido'}
                                        </span>
                                      )}
                                      {t.codmod === dados.codmod && (
                                        <span className="badge badge-success">este modelo</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}

                        <Botoes>
                          <button type="button" className="btn btn-sm btn-primary" onClick={salvarTurmas}>Salvar turmas</button>
                        </Botoes>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* ── Modal: novo modelo ─────────────────────────────────────────── */}
      <Modal show={showNovo} onHide={() => setShowNovo(false)} centered>
        <Modal.Header closeButton><Modal.Title>Novo modelo de agenda</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Código (até 2 caracteres)</Form.Label>
            <Form.Control
              type="text"
              maxLength={2}
              value={novo.codmod}
              onChange={e => setNovo({ ...novo, codmod: e.target.value.toUpperCase() })}
            />
            <Form.Text className="text-muted">
              Precisa ser único no período. Um dígito só vira "01", "02"...
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              type="text"
              maxLength={120}
              value={novo.desmod}
              onChange={e => setNovo({ ...novo, desmod: e.target.value })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowNovo(false)}>Cancelar</Button>
          <Button variant="primary" size="sm" onClick={salvarNovo}>Criar</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal: respostas do item tipo NOTA ─────────────────────────── */}
      <Modal show={!!escalaAberta} onHide={() => setEscalaAberta(null)} centered>
        <Modal.Header closeButton><Modal.Title>Respostas do item</Modal.Title></Modal.Header>
        <Modal.Body>
          {escalaAberta && (
            <>
              <p className="text-muted" style={{ fontSize: 13 }}>
                Texto que o professor e o responsável veem em cada valor da escala
                <strong> deste item</strong>. Deixe em branco a opção que não quiser usar —
                o aplicativo lista apenas as que tiverem texto.
              </p>

              {CODS_NOTA.map(cod => (
                <div className="form-row align-items-center mb-2" key={cod}>
                  <div className="col-auto">
                    <span className="badge badge-primary" style={{ fontSize: 14, width: 28, padding: 6 }}>{cod}</span>
                  </div>
                  <div className="col">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      maxLength={50}
                      placeholder={`${NOTA_PADRAO[cod]} (opcional)`}
                      value={escalaAberta.escala[cod] || ''}
                      onChange={e => alterarEscala(cod, e.target.value)}
                    />
                  </div>
                </div>
              ))}

              <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                  {opcoesPreenchidas(escalaAberta.escala)} de 5 preenchidas
                </small>
                <div>
                  <button type="button" className="btn btn-sm btn-outline-secondary mr-2" onClick={limparEscala}>
                    Limpar
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={usarTextosPadrao}>
                    Usar textos padrão
                  </button>
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setEscalaAberta(null)}>Cancelar</Button>
          <Button variant="primary" size="sm" onClick={confirmarEscala}>Aplicar</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal: duplicar ────────────────────────────────────────────── */}
      <Modal show={showDuplicar} onHide={() => setShowDuplicar(false)} centered>
        <Modal.Header closeButton><Modal.Title>Duplicar modelo {dados.codmod}</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="text-muted" style={{ fontSize: 13 }}>
            Copia os itens e a escala de nota. As turmas não são copiadas.
          </p>
          <Form.Group>
            <Form.Label>Código do novo modelo</Form.Label>
            <Form.Control
              type="text"
              maxLength={2}
              value={duplicar.novoCodmod}
              onChange={e => setDuplicar({ ...duplicar, novoCodmod: e.target.value.toUpperCase() })}
            />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              type="text"
              maxLength={120}
              value={duplicar.novoDesmod}
              onChange={e => setDuplicar({ ...duplicar, novoDesmod: e.target.value })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowDuplicar(false)}>Cancelar</Button>
          <Button variant="primary" size="sm" onClick={confirmarDuplicar}>Duplicar</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal: excluir ─────────────────────────────────────────────── */}
      <Modal show={showExcluir} onHide={() => setShowExcluir(false)} centered>
        <Modal.Header closeButton><Modal.Title>Excluir modelo</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>
            Excluir o modelo <strong>{dados.codmod} - {dados.desmod}</strong>?
          </p>
          <p className="mb-0 text-danger" style={{ fontSize: 13 }}>
            Os itens e a escala de nota são apagados e as turmas ficam sem modelo.
            As agendas já lançadas para os alunos continuam gravadas.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowExcluir(false)}>Cancelar</Button>
          <Button variant="danger" size="sm" onClick={confirmarExcluir}>Excluir</Button>
        </Modal.Footer>
      </Modal>

      <Footer />
    </Container>
  );
}

export default connect(state => ({
  usuario: state.usuario,
  escola: state.escola,
  periodo: state.periodo,
  sistema: state.sistema,
}))(AgendaModelos);
